'use client';

import React, { FC, useState, Fragment, MouseEvent } from 'react';

import classNames from 'classnames';

import { TypeExplorerSlice } from '@/core/generated-types';
import { CLICKED_ITEM_TYPES, trackItemClickedEvent } from '@/utils/rudderstack';
import { getPaddingClass } from '@/utils/paddingHelpers';

interface ExplorerSwitchProps {
  slices: TypeExplorerSlice[] | undefined;
  theme: 'primary' | 'red' | 'default';
  newAnchorStyle?: boolean;
  enableFluidGrid?: boolean;
  eventPageLocation?: string;
  backgroundColor?: { fields: { color: string } };
  topPadding?: number;
  bottomPadding?: number;
}

const anchorColorStyle = {
  primary: 'text-blue-deep hover:text-[#131C66] active:text-[#131C66]',
  red: 'text-red-v2 hover:text-red-v2-hover active:text-red-v2-hover',
  default: 'text-blue-deep hover:text-[#131C66] active:text-[#131C66]',
};

const ExplorerSwitch: FC<ExplorerSwitchProps> = ({
  slices,
  theme,
  newAnchorStyle = false,
  enableFluidGrid,
  eventPageLocation,
  backgroundColor,
  topPadding = 6,
  bottomPadding = 6,
}) => {
  const topPaddingClass = getPaddingClass(topPadding, 'top');
  const bottomPaddingClass = getPaddingClass(bottomPadding, 'bottom');

  const sliceSwitchItems = slices?.map((slice: TypeExplorerSlice) => ({
    anchorId: slice?.fields?.anchorId,
    anchorText: slice?.fields?.anchorText,
  }));

  const [activeAnchorId, setActiveAnchorId] = useState<string | undefined>(
    sliceSwitchItems?.[0]?.anchorId,
  );

  const numberOfItems = sliceSwitchItems?.length || 0;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>, anchorId: string) => {
    e.preventDefault();

    const targetElement = document.getElementById(anchorId);

    trackItemClickedEvent({
      url: window.location.href,
      type: CLICKED_ITEM_TYPES.ANCHOR_LINK,
      pageLocation: eventPageLocation ?? 'Unspecified',
      cta: e.currentTarget.textContent ?? 'Unspecified',
    });

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <div
      className={classNames(
        'mx-auto flex text-d-base md:gap-4 md:text-h-x4 font-bold',
        topPaddingClass,
        bottomPaddingClass,
        {
          'max-w-[100vw] w-fit overflow-x-scroll mx-auto no-scrollbar min-w-full flex justify-center border-b border-gray-200':
            newAnchorStyle,
          'flex-wrap justify-center': !newAnchorStyle,
          'lg:hidden': enableFluidGrid,
          [`bg-${backgroundColor?.fields?.color}`]:
            !!backgroundColor?.fields?.color,
        },
      )}
    >
      {sliceSwitchItems &&
        sliceSwitchItems.map(({ anchorText, anchorId }, index) => {
          const isActiveAnchor = activeAnchorId === anchorId;

          const containerClass = {
            'bg-anchorLinkWatercolorBg bg-[length:88px] bg-[center_top_2px] bg-no-repeat':
              isActiveAnchor && theme === 'default' && !newAnchorStyle,
            'flex justify-center items-center font-bold whitespace-nowrap text-base lg:text-lg mx-2 lg:mx-1 h-[48px] lg:h-[58px] px-1 lg:px-2':
              newAnchorStyle,
            'border-b-4 border-blue-deep text-blue-deep':
              isActiveAnchor && newAnchorStyle,
          };

          const itemClass = {
            [`flex h-8 md:h-[80px] items-center px-2 lg:px-4 underline ${anchorColorStyle[theme]}`]:
              !newAnchorStyle,
          };

          // Insert a break to force a new row and avoid displaying an element alone on the last row on mobile.
          const isAfterItemThreeOutOfFive = index === 3 && numberOfItems === 5;

          const rowBreakForMobileViewport = isAfterItemThreeOutOfFive && (
            <div className="w-[100%] basis-auto md:hidden" />
          );

          return (
            <Fragment key={anchorId}>
              {rowBreakForMobileViewport}
              <div className="flex justify-center">
                <div
                  className={classNames(containerClass)}
                  onClick={() => setActiveAnchorId(anchorId)}
                >
                  <a
                    href={`#${anchorId}`}
                    onClick={(e) => handleClick(e, anchorId)}
                    className={classNames(itemClass)}
                  >
                    {anchorText}
                  </a>
                </div>
              </div>
            </Fragment>
          );
        })}
    </div>
  );
};

export default React.memo(ExplorerSwitch);
